import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { Button } from "@/components/flexkit/Button";
import { Combobox } from "@/components/flexkit/combobox/Combobox";
import { CursorPagination } from "@/components/flexkit/CursorPagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import { MemberCombobox } from "@/components/members/MemberCombobox";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { ProjectCombobox } from "@/components/projects/ProjectCombobox";
import { Tooltip } from "@/components/Tooltip";
import { useCursorPagination } from "@/hooks/use-cursor-pagination";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useDeleteEnvironment,
    useListEnvironmentInventory,
    useStopEnvironment,
    type PlainEnvironment,
    type UseListEnvironmentInventoryParams,
} from "@/queries/environment-queries";
import {
    getMemberFromCache,
    useGetOrganizationPolicies,
    usePrefetchMembersByIds,
    type PlainOrganizationMember,
} from "@/queries/organization-queries";
import { useProject } from "@/queries/project-queries";
import { useListRunners, type PlainRunner } from "@/queries/runner-queries";
import {
    getServiceAccountFromCache,
    usePrefetchServiceAccountsByIds,
    type PlainServiceAccount,
} from "@/queries/service-account-queries";
import { getRepoUrl } from "@/repo-url";
import { EnvironmentPhaseTag } from "@/routes/environments/EnvironmentPhaseTag";
import { effectiveState } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { Timestamp } from "@bufbuild/protobuf";
import { useQueryClient } from "@tanstack/react-query";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { CircleAlert } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";

export const Inventory: FC = () => {
    const [searchParams] = useSearchParams();
    const runnerFilter = searchParams.get("runner") || undefined;
    const runnerKind = useMemo(() => {
        for (const opt of runnerKindOptions) {
            if (runnerFilter === opt.key) {
                return opt.kind;
            }
        }
        return undefined;
    }, [runnerFilter]);
    const runnerID = useMemo(() => {
        // If the runner filter is not a runner kind, it's a runner ID
        return runnerKind ? undefined : runnerFilter;
    }, [runnerKind, runnerFilter]);
    const creatorID = searchParams.get("creator") || undefined;

    const { data: runners, isLoading: isLoadingRunners } = useListRunners({});
    const { data: policiesData, isLoading: isLoadingPolicies } = useGetOrganizationPolicies();
    const [filterValue, setFilterValue] = useState<FilterValue>({ runnerID, creatorID, runnerKind });

    const handleFilterChange = useCallback((value: FilterValue) => {
        setFilterValue(value);
    }, []);

    const maxEnvLifetime = policiesData?.maximumEnvironmentLifetime?.seconds
        ? Number(policiesData.maximumEnvironmentLifetime.seconds)
        : 0;
    const listEnvironmentFilter = useMemo<UseListEnvironmentInventoryParams>(() => {
        return {
            runnerID: filterValue.runnerID,
            creatorID: filterValue.creatorID,
            projectID: filterValue.projectID,
            phase: filterValue.phase,
            runnerKind: filterValue.runnerKind,
            createdBefore:
                filterValue.status === LIFETIME_POLICY_EXCEEDED && maxEnvLifetime > 0
                    ? new Date(Date.now() - maxEnvLifetime * 1000)
                    : undefined,
        };
    }, [filterValue, maxEnvLifetime]);

    const isLoading = isLoadingRunners || isLoadingPolicies;
    if (isLoading) {
        return <LoadingState />;
    }

    return (
        <div className="flex h-full flex-col">
            <div className="text-base">Inventory of running and stopped environments in your organization.</div>
            <Filter
                runners={runners || []}
                initialValue={{ runnerID, creatorID, runnerKind }}
                onFilterChange={handleFilterChange}
                className="pt-4"
            />
            <InventoryTable
                filterValue={listEnvironmentFilter}
                runners={runners || []}
                maxEnvLifetime={maxEnvLifetime}
                className="flex flex-grow flex-col pt-4"
            />
        </div>
    );
};

type FilterValue = {
    runnerID?: string;
    creatorID?: string;
    projectID?: string;
    phase?: EnvironmentPhase;
    runnerKind?: RunnerKind;
    status?: string;
};

type FilterProps = {
    initialValue: FilterValue;
    runners: PlainRunner[];
    onFilterChange?: (value: FilterValue) => void;
};

const allPhases: Record<string, EnvironmentPhase> = {
    Creating: EnvironmentPhase.CREATING,
    Starting: EnvironmentPhase.STARTING,
    Running: EnvironmentPhase.RUNNING,
    Updating: EnvironmentPhase.UPDATING,
    Stopping: EnvironmentPhase.STOPPING,
    Stopped: EnvironmentPhase.STOPPED,
    Deleting: EnvironmentPhase.DELETING,
    Deleted: EnvironmentPhase.DELETED,
};

const LIFETIME_POLICY_EXCEEDED = "Lifetime Policy Exceeded";
const allStatuses: Record<string, string> = {
    [LIFETIME_POLICY_EXCEEDED]: LIFETIME_POLICY_EXCEEDED,
};

// Add additional filter items for these kinds in the runner filter.
// Allows selecting all local or all remote runners.
const runnerKindOptions = [
    {
        kind: RunnerKind.LOCAL,
        key: "local",
        name: "All Ona Desktop runners",
    },
    {
        kind: RunnerKind.REMOTE,
        key: "remote",
        name: "All remote runners",
    },
];

type FilterItem = { id: string; name: string };

const allRunnersItem: FilterItem = { id: "all", name: "All runners" };
const allPhasesItem: FilterItem = { id: "all", name: "All phases" };
const allStatusesItem: FilterItem = { id: "all", name: "All statuses" };

const Filter: FC<FilterProps & PropsWithClassName> = ({ className, runners, initialValue, onFilterChange }) => {
    const [prevFilterValue, setPrevFilterValue] = useState<FilterValue>(initialValue);

    const [selectedRunnerID, setSelectedRunnerID] = useState<string | undefined>(initialValue.runnerID);
    const [selectedMemberID, setSelectedMemberID] = useState<string | undefined>(initialValue.creatorID);
    const [selectedProjectID, setSelectedProjectID] = useState<string | undefined>(initialValue.projectID);
    const [selectedRunnerKind, setSelectedRunnerKind] = useState<RunnerKind | undefined>(initialValue.runnerKind);
    const [selectedPhase, setSelectedPhase] = useState<string | undefined>();
    const [selectedStatus, setSelectedStatus] = useState<string | undefined>();

    const setSelectedRunner = useCallback((value: string) => {
        if (value === "all") {
            setSelectedRunnerKind(undefined);
            setSelectedRunnerID(undefined);
            return;
        }

        for (const opt of runnerKindOptions) {
            if (value === opt.key) {
                // Value is a runner kind.
                setSelectedRunnerKind(opt.kind);
                setSelectedRunnerID(undefined);
                return;
            }
        }

        // If the value is not a runner kind, it's a runner ID.
        setSelectedRunnerKind(undefined);
        setSelectedRunnerID(value);
    }, []);

    const debouncedFilterChanged = useDebouncedCallback((filter: FilterValue) => {
        if (!onFilterChange) {
            return;
        }
        onFilterChange(filter);
    }, 100);

    useEffect(() => {
        debouncedFilterChanged({ ...prevFilterValue });
    }, [prevFilterValue, debouncedFilterChanged]);

    useEffect(() => {
        setPrevFilterValue((prev) => {
            if (
                selectedRunnerID !== prev.runnerID ||
                selectedMemberID !== prev.creatorID ||
                selectedProjectID !== prev.projectID ||
                selectedRunnerKind !== prev.runnerKind ||
                selectedPhase !== prev.phase ||
                selectedStatus !== prev.status
            ) {
                return {
                    ...prev,
                    runnerID: selectedRunnerID,
                    creatorID: selectedMemberID,
                    projectID: selectedProjectID,
                    runnerKind: selectedRunnerKind,
                    phase: selectedPhase ? allPhases[selectedPhase] : undefined,
                    status: selectedStatus ? allStatuses[selectedStatus] : undefined,
                };
            }
            return prev;
        });
    }, [selectedRunnerID, selectedMemberID, selectedProjectID, selectedRunnerKind, selectedPhase, selectedStatus]);

    const runnerItems = useMemo<FilterItem[]>(() => {
        const items: FilterItem[] = [allRunnersItem];
        for (const opt of runnerKindOptions) {
            items.push({ id: opt.key, name: opt.name });
        }
        for (const r of runners) {
            items.push({ id: r.runnerId, name: r.name });
        }
        return items;
    }, [runners]);

    const phaseItems = useMemo<FilterItem[]>(() => {
        return [allPhasesItem, ...Object.keys(allPhases).map((p) => ({ id: p, name: p }))];
    }, []);

    const statusItems = useMemo<FilterItem[]>(() => {
        return [allStatusesItem, ...Object.keys(allStatuses).map((s) => ({ id: s, name: s }))];
    }, []);

    const selectedRunner = useMemo(() => {
        for (const opt of runnerKindOptions) {
            if (selectedRunnerKind === opt.kind) {
                return opt.key;
            }
        }

        return selectedRunnerID ?? "all";
    }, [selectedRunnerID, selectedRunnerKind]);

    const selectedRunnerName = useMemo(() => {
        const item = runnerItems.find((r) => r.id === selectedRunner);
        return item?.name ?? "All runners";
    }, [runnerItems, selectedRunner]);

    const handleProjectChange = useCallback(
        (value: string) => {
            setSelectedProjectID(value === "all" ? undefined : value);
        },
        [setSelectedProjectID],
    );

    const handleMemberChange = useCallback(
        (value: string) => {
            setSelectedMemberID(value === "all" ? undefined : value);
        },
        [setSelectedMemberID],
    );

    const handlePhaseChange = useCallback(
        (value: string) => {
            setSelectedPhase(value === "all" ? undefined : value);
        },
        [setSelectedPhase],
    );

    const handleStatusChange = useCallback(
        (value: string) => {
            setSelectedStatus(value === "all" ? undefined : value);
        },
        [setSelectedStatus],
    );

    return (
        <div className={cn("grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5", className)}>
            <ProjectCombobox
                selectedProject={selectedProjectID ?? null}
                onProjectChange={handleProjectChange}
                placeholder="All projects"
            />
            <MemberCombobox
                selectedMember={selectedMemberID ?? null}
                onMemberChange={handleMemberChange}
                includeServiceAccounts
            />
            <Combobox value={selectedRunner} onValueChange={setSelectedRunner} filterPlaceholder="Search runners...">
                <Combobox.Value>
                    <Combobox.ValueLabel>{selectedRunnerName}</Combobox.ValueLabel>
                </Combobox.Value>
                <Combobox.Popover>
                    <Combobox.List
                        items={runnerItems}
                        searchKeys={["name"]}
                        noMatchesComponent={<Combobox.Empty>No runners found</Combobox.Empty>}
                    >
                        {(item) => (
                            <Combobox.ListItem key={item.id} value={item.id}>
                                <Combobox.ListItemTitle>{item.name}</Combobox.ListItemTitle>
                            </Combobox.ListItem>
                        )}
                    </Combobox.List>
                </Combobox.Popover>
            </Combobox>
            <Combobox
                value={selectedPhase ?? "all"}
                onValueChange={handlePhaseChange}
                filterPlaceholder="Search phases..."
            >
                <Combobox.Value>
                    <Combobox.ValueLabel>{selectedPhase ?? "All phases"}</Combobox.ValueLabel>
                </Combobox.Value>
                <Combobox.Popover>
                    <Combobox.List
                        items={phaseItems}
                        searchKeys={["name"]}
                        noMatchesComponent={<Combobox.Empty>No phases found</Combobox.Empty>}
                    >
                        {(item) => (
                            <Combobox.ListItem key={item.id} value={item.id}>
                                <Combobox.ListItemTitle>{item.name}</Combobox.ListItemTitle>
                            </Combobox.ListItem>
                        )}
                    </Combobox.List>
                </Combobox.Popover>
            </Combobox>
            <Combobox
                value={selectedStatus ?? "all"}
                onValueChange={handleStatusChange}
                filterPlaceholder="Search statuses..."
            >
                <Combobox.Value>
                    <Combobox.ValueLabel>{selectedStatus ?? "All statuses"}</Combobox.ValueLabel>
                </Combobox.Value>
                <Combobox.Popover>
                    <Combobox.List
                        items={statusItems}
                        searchKeys={["name"]}
                        noMatchesComponent={<Combobox.Empty>No statuses found</Combobox.Empty>}
                    >
                        {(item) => (
                            <Combobox.ListItem key={item.id} value={item.id}>
                                <Combobox.ListItemTitle>{item.name}</Combobox.ListItemTitle>
                            </Combobox.ListItem>
                        )}
                    </Combobox.List>
                </Combobox.Popover>
            </Combobox>
        </div>
    );
};

type InventoryTableProps = {
    filterValue: UseListEnvironmentInventoryParams;
    runners: PlainRunner[];
    maxEnvLifetime: number;
};

const InventoryTable: FC<InventoryTableProps & PropsWithClassName> = ({
    filterValue,
    className,
    runners,
    maxEnvLifetime,
}) => {
    const queryClient = useQueryClient();
    const cursor = useCursorPagination();

    // Reset cursor when filters change
    const filterKey = JSON.stringify(filterValue);
    const prevFilterKeyRef = useRef(filterKey);
    useEffect(() => {
        if (prevFilterKeyRef.current !== filterKey) {
            prevFilterKeyRef.current = filterKey;
            cursor.reset();
        }
    }, [filterKey, cursor]);

    const { data, isFetching } = useListEnvironmentInventory({ ...filterValue, token: cursor.currentToken });
    const environments = useMemo(() => data?.environments ?? [], [data?.environments]);

    // Extract unique creator IDs from environments, separated by principal type
    const { memberIds, serviceAccountIds } = useMemo(() => {
        const members = new Set<string>();
        const serviceAccounts = new Set<string>();
        for (const env of environments) {
            const creator = env.metadata?.creator;
            if (creator?.id) {
                if (creator.principal === Principal.USER) {
                    members.add(creator.id);
                } else if (creator.principal === Principal.SERVICE_ACCOUNT) {
                    serviceAccounts.add(creator.id);
                }
            }
        }
        return {
            memberIds: Array.from(members),
            serviceAccountIds: Array.from(serviceAccounts),
        };
    }, [environments]);

    // Prefetch members and service accounts - this populates individual caches
    usePrefetchMembersByIds(memberIds, { enabled: memberIds.length > 0 });
    usePrefetchServiceAccountsByIds(serviceAccountIds, { enabled: serviceAccountIds.length > 0 });

    // Read member/service account data from cache
    // These will be populated by the prefetch hooks above
    const getMember = useCallback(
        (userId: string | undefined): PlainOrganizationMember | undefined => {
            if (!userId) return undefined;
            return getMemberFromCache(queryClient, userId);
        },
        [queryClient],
    );

    const getServiceAccount = useCallback(
        (serviceAccountId: string | undefined): PlainServiceAccount | undefined => {
            if (!serviceAccountId) return undefined;
            return getServiceAccountFromCache(queryClient, serviceAccountId);
        },
        [queryClient],
    );

    const runnersMap = useMemo(() => {
        const map = new Map<string | undefined, PlainRunner>();
        for (const r of runners) {
            map.set(r.runnerId, r);
        }
        return map;
    }, [runners]);

    const isEmpty = environments.length === 0;
    if (isEmpty) {
        const anyFilters =
            filterValue.runnerID ||
            filterValue.creatorID ||
            filterValue.projectID ||
            filterValue.phase ||
            filterValue.createdBefore;

        if (anyFilters) {
            return (
                <div className="mt-8">
                    <EmptyStateCard
                        title="No matches"
                        description="No environments matching the filters."
                        data-testid="no-matches-empty-state"
                    />
                </div>
            );
        } else {
            return (
                <div className="mt-8">
                    <EmptyStateCard
                        title="It's quiet here"
                        description="Currently, there are no environments in your organization."
                        data-testid="no-environments-empty-state"
                    />
                </div>
            );
        }
    }

    return (
        <div className={cn("flex flex-col", className)}>
            <div>
                <Table>
                    <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                        <TableRow className="h-8 text-base text-content-strong">
                            <TableHead className="w-[22%] pl-3 xl:w-[26%]">Project</TableHead>
                            <TableHead className="pl-3">Member</TableHead>
                            <TableHead className="pl-3">Runner</TableHead>
                            <TableHead className="pl-3">Created</TableHead>
                            <TableHead className="pl-3">Last started</TableHead>
                            <TableHead className="pl-3">Phase</TableHead>
                            <TableHead className="w-[80px]" />
                        </TableRow>
                    </TableHeader>
                    <TableBody className="border-t border-border-light text-base">
                        {environments.map((environment) => {
                            const creator = environment.metadata?.creator;
                            const member = creator?.principal === Principal.USER ? getMember(creator.id) : undefined;
                            const serviceAccount =
                                creator?.principal === Principal.SERVICE_ACCOUNT
                                    ? getServiceAccount(creator.id)
                                    : undefined;
                            return (
                                <InventoryItem
                                    key={"item-" + environment.id}
                                    environment={environment}
                                    member={member}
                                    serviceAccount={serviceAccount}
                                    runner={runnersMap.get(environment.metadata?.runnerId)}
                                    maxEnvLifetime={maxEnvLifetime}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
            <CursorPagination
                onNext={() => {
                    if (data?.nextToken) {
                        cursor.goToNextPage(data.nextToken);
                    }
                }}
                onPrevious={cursor.goToPreviousPage}
                hasNextPage={!!data?.nextToken}
                hasPreviousPage={cursor.hasPreviousPage}
                isLoading={isFetching}
                currentItemsCount={environments.length}
            />
        </div>
    );
};

type InventoryItemProps = {
    environment: PlainEnvironment;
    member?: PlainOrganizationMember;
    serviceAccount?: PlainServiceAccount;
    runner?: PlainRunner;
    maxEnvLifetime: number;
};

const InventoryItem: FC<InventoryItemProps & PropsWithClassName> = ({
    environment,
    runner,
    member,
    serviceAccount,
    maxEnvLifetime,
}) => {
    const creatorName = member?.fullName || serviceAccount?.name;
    const creatorAvatarUrl = member?.avatarUrl;
    const [showModal, setShowModal] = useState<"stop" | "delete" | "force-delete" | undefined>(undefined);

    const { data: project } = useProject(environment.metadata?.projectId);

    const isLifetimeExceeded = useMemo(() => {
        if (maxEnvLifetime <= 0) {
            return false;
        }
        if (!environment.metadata?.createdAt) {
            return false;
        }
        const createdAt = new Timestamp(environment.metadata.createdAt).toDate().getTime();
        return Date.now() - createdAt > maxEnvLifetime * 1000;
    }, [environment.metadata?.createdAt, maxEnvLifetime]);

    const createdTime = useMemo(
        () =>
            environment.metadata?.createdAt
                ? new Timestamp(environment.metadata?.createdAt).toDate().toLocaleDateString()
                : "",
        [environment.metadata?.createdAt],
    );

    const lastStarted = useMemo(
        () =>
            environment.metadata?.lastStartedAt
                ? new Timestamp(environment.metadata?.lastStartedAt).toDate().toLocaleDateString()
                : "",
        [environment.metadata?.lastStartedAt],
    );

    const state = useMemo(() => {
        if (environment) {
            return effectiveState(environment);
        }
        return;
    }, [environment]);

    const { toast } = useToast();

    const stopEnvironment = useStopEnvironment();
    const handleStop = useCallback(() => {
        stopEnvironment.mutate(environment.id, {
            onError: (e) => {
                toast({
                    title: "Failed to stop environment",
                    description: formatError(e),
                });
            },
        });
    }, [stopEnvironment, toast, environment.id]);

    const deleteEnvironment = useDeleteEnvironment();
    const handleDelete = useCallback(() => {
        deleteEnvironment.mutate(
            { environmentId: environment.id, force: false },
            {
                onError: (e) => {
                    toast({
                        title: "Failed to delete environment",
                        description: formatError(e),
                    });
                },
            },
        );
    }, [deleteEnvironment, environment.id, toast]);

    const handleForceDelete = useCallback(() => {
        deleteEnvironment.mutate(
            { environmentId: environment.id, force: true },
            {
                onError: (e) => {
                    toast({
                        title: "Failed to force delete environment",
                        description: formatError(e),
                    });
                },
            },
        );
    }, [deleteEnvironment, environment.id, toast]);

    const projectOrRepo = useMemo(() => {
        if (project?.metadata?.name) {
            return project?.metadata?.name;
        }

        return getRepoUrl(environment)?.repoUrl?.replace("https://github.com/", "") || "From scratch";
    }, [environment, project?.metadata?.name]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(environment.id);
        toast({
            title: `Environment ID copied to clipboard`,
            description: environment.id,
        });
    }, [toast, environment.id]);

    return (
        <>
            <TableRow className="h-16 border-border-light">
                <TableCell>
                    <Text className="truncate text-base">{projectOrRepo}</Text>
                </TableCell>
                <TableCell>
                    <div className="inline-flex grow items-center space-x-2">
                        <Avatar size={32} className="hidden xl:inline-flex">
                            {creatorAvatarUrl && (
                                <AvatarImage src={creatorAvatarUrl} alt={`${creatorName || "Unknown"}'s avatar`} />
                            )}
                            <AvatarFallback>
                                <AvatarInitials name={creatorName || "Unknown"} size={32} />
                            </AvatarFallback>
                        </Avatar>
                        <Text className="text-base">{creatorName}</Text>
                    </div>
                </TableCell>
                <TableCell>
                    <Text className="text-base">{runner?.name}</Text>
                </TableCell>
                <TableCell>
                    <div className="flex items-center gap-1">
                        <Text className={cn("truncate text-base", isLifetimeExceeded && "text-content-red")}>
                            {createdTime}
                        </Text>
                        {isLifetimeExceeded && (
                            <Tooltip content="Environment lifetime exceeded">
                                <CircleAlert className="h-4 w-4 text-content-red" />
                            </Tooltip>
                        )}
                    </div>
                </TableCell>
                <TableCell>
                    <Text className="truncate text-base">{lastStarted}</Text>
                </TableCell>
                <TableCell>
                    {state && (
                        <>
                            <div className="hidden max-w-[140px] xl:block">
                                <EnvironmentPhaseTag state={state} truncate />
                            </div>
                            <div className="block xl:hidden">
                                <EnvironmentPhaseTag state={state} variant="dot" />
                            </div>
                        </>
                    )}
                </TableCell>
                <TableCell>
                    <div className="flex flex-row justify-end">
                        <DropdownActions>
                            <DropdownMenuItem
                                onClick={handleCopyId}
                                data-tracking-id="copy-environment-id-environment-inventory-item"
                            >
                                Copy ID
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                disabled={environment.status?.phase !== EnvironmentPhase.RUNNING}
                                onClick={() => setShowModal("stop")}
                                data-tracking-id="show-stop-environment-modal-environment-inventory-item"
                            >
                                Stop
                            </DropdownMenuItem>
                            {/* Show Delete, when the Environment has not yet been requested to delete */}
                            {environment.spec?.desiredPhase !== EnvironmentPhase.DELETED && (
                                <DropdownMenuItem
                                    onClick={() => setShowModal("delete")}
                                    className="text-red-500"
                                    data-tracking-id="show-delete-environment-modal-environment-inventory-item"
                                >
                                    Delete
                                </DropdownMenuItem>
                            )}
                            {/* Show Force Delete, when the Environment is in Deleting state */}
                            {environment.spec?.desiredPhase === EnvironmentPhase.DELETED && (
                                <DropdownMenuItem
                                    onClick={() => setShowModal("force-delete")}
                                    className="text-red-500"
                                    data-tracking-id="show-force-delete-environment-modal-environment-inventory-item"
                                >
                                    Force Delete
                                </DropdownMenuItem>
                            )}
                        </DropdownActions>
                    </div>
                </TableCell>
            </TableRow>
            {showModal === "stop" && (member || serviceAccount) && runner && (
                <StopEnvironmentModal
                    environment={environment}
                    member={member}
                    serviceAccount={serviceAccount}
                    runner={runner}
                    onContinue={() => {
                        setShowModal(undefined);
                        handleStop();
                    }}
                    onClose={() => {
                        setShowModal(undefined);
                    }}
                />
            )}
            {showModal === "delete" && (member || serviceAccount) && runner && (
                <DeleteEnvironmentModal
                    environment={environment}
                    member={member}
                    serviceAccount={serviceAccount}
                    runner={runner}
                    onContinue={() => {
                        setShowModal(undefined);
                        handleDelete();
                    }}
                    onClose={() => {
                        setShowModal(undefined);
                    }}
                />
            )}
            {showModal === "force-delete" && (member || serviceAccount) && runner && (
                <ForceDeleteEnvironmentModal
                    environment={environment}
                    member={member}
                    serviceAccount={serviceAccount}
                    runner={runner}
                    onContinue={() => {
                        setShowModal(undefined);
                        handleForceDelete();
                    }}
                    onClose={() => {
                        setShowModal(undefined);
                    }}
                />
            )}
        </>
    );
};

const StopEnvironmentModal: FC<
    {
        onContinue: () => void;
        onClose: () => void;
    } & InventoryBannerProps
> = (p) => {
    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.InventoryStopEnvironmentModal}>
                <DialogHeader>
                    <DialogTitle>Stop environment</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogDescription>
                    {p.serviceAccount ? (
                        <span>This will stop the service account&apos;s environment.</span>
                    ) : (
                        <span>This will stop the user&apos;s environment. They won&apos;t lose any changes.</span>
                    )}
                    <InventoryBanner {...p} className="mt-4" />
                    {p.member && (
                        <div className="mt-4">
                            Tip: You may want to let {p.member.fullName} know that you&apos;ve stopped their
                            environment.
                        </div>
                    )}
                </DialogDescription>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={p.onClose}
                            data-tracking-id="cancel-stop-environment-inventory-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        autoFocus={true}
                        variant="destructive"
                        onClick={(e) => {
                            e.preventDefault();
                            p.onContinue();
                        }}
                        data-tracking-id="confirm-stop-environment-inventory-modal"
                    >
                        Stop
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const DeleteEnvironmentModal: FC<
    {
        onContinue: () => void;
        onClose: () => void;
    } & InventoryBannerProps
> = (p) => {
    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.InventoryDeleteEnvironmentModal}>
                <DialogHeader>
                    <DialogTitle>Delete environment</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogDescription>
                    {p.serviceAccount ? (
                        <span>This will delete the service account&apos;s environment.</span>
                    ) : (
                        <span>
                            This will delete the user&apos;s environment. They may lose any uncommitted changes.
                        </span>
                    )}
                    <InventoryBanner {...p} className="mt-4" />
                    {p.member && (
                        <div className="mt-4">
                            Tip: You may want to let {p.member.fullName} know that you&apos;ve deleted their
                            environment.
                        </div>
                    )}
                </DialogDescription>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={p.onClose}
                            data-tracking-id="cancel-delete-environment-inventory-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        autoFocus={true}
                        variant="destructive"
                        onClick={(e) => {
                            e.preventDefault();
                            p.onContinue();
                        }}
                        data-tracking-id="confirm-delete-environment-inventory-modal"
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const ForceDeleteEnvironmentModal: FC<
    {
        onContinue: () => void;
        onClose: () => void;
    } & InventoryBannerProps
> = (p) => {
    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent
                className="max-w-lg"
                data-track-location={TrackLocations.InventoryForceDeleteEnvironmentModal}
            >
                <DialogHeader>
                    <DialogTitle>Irreversible Action</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogDescription>
                    {p.serviceAccount ? (
                        <span>
                            This will force delete the service account&apos;s environment, which may leave some
                            infrastructure or files behind that require manual cleanup.
                        </span>
                    ) : (
                        <span>
                            This will force delete the user&apos;s environment, which may leave some infrastructure or
                            files behind that require manual cleanup.
                        </span>
                    )}
                    <InventoryBanner {...p} className="mt-4" />
                    {p.member && (
                        <div className="mt-4">
                            Tip: You may want to let {p.member.fullName} know that you&apos;ve force deleted their
                            environment.
                        </div>
                    )}
                </DialogDescription>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={p.onClose}
                            data-tracking-id="cancel-force-delete-environment-force-delete-environment-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        autoFocus={true}
                        variant="destructive"
                        onClick={(e) => {
                            e.preventDefault();
                            p.onContinue();
                        }}
                        data-tracking-id="confirm-force-delete-environment-force-delete-environment-modal"
                    >
                        Force Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

type InventoryBannerProps = {
    environment: PlainEnvironment;
    member?: PlainOrganizationMember;
    serviceAccount?: PlainServiceAccount;
    runner: PlainRunner;
} & PropsWithClassName;

const InventoryBanner: FC<InventoryBannerProps> = (p) => {
    const creatorName = p.member?.fullName || p.serviceAccount?.name;
    const creatorAvatarUrl = p.member?.avatarUrl;
    const { data: project } = useProject(p.environment.metadata?.projectId);

    const projectOrRepo = useMemo(() => {
        if (project?.metadata?.name) {
            return project?.metadata?.name;
        }

        return getRepoUrl(p.environment)?.repoUrl?.replace("https://github.com/", "") || "From scratch";
    }, [p.environment, project?.metadata?.name]);

    return (
        <div
            className={cn(
                "flex w-full flex-row rounded-lg border border-solid border-border-light bg-surface-secondary px-4 py-2",
                p.className,
            )}
        >
            <div className="flex flex-grow flex-col">
                <Text className="text-base font-bold">{projectOrRepo}</Text>
                <Text className="text-base">{p.runner.name}</Text>
            </div>
            <div className="inline-flex items-center space-x-2">
                <Avatar size={32}>
                    {creatorAvatarUrl && (
                        <AvatarImage src={creatorAvatarUrl} alt={`${creatorName || "Unknown"}'s avatar`} />
                    )}
                    <AvatarFallback>
                        <AvatarInitials name={creatorName || "Unknown"} size={32} />
                    </AvatarFallback>
                </Avatar>
                <Text className="text-base">{creatorName}</Text>
            </div>
        </div>
    );
};
